import DialogModal from '@/components/DialogModal';
import usePersistedState from '@/hooks/usePersistedState';
import {
  useCronofyAuthUrl,
  useCronofyCheckCalendarPermissions,
  useCronofyDisconnectProfile,
  useCronofyProfiles,
  useCronofyRequestExtendedPermissions,
  useCronofySetInboundSync,
  useCronofySetOutboundSync,
} from '@/services/cronofy/hooks';
import { IconAlert, IconArrowDownFull, IconBlock, IconPlus } from '@/theme/icons';
import { providerIcon } from '@/utils/calendar';
import { CronofyExtendedPermissionsManager, getCalendarsNeedingPermissions } from '@/utils/cronofyExtendedPermissions';
import rem from '@/utils/rem';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Icon,
  IconButton,
  Link,
  ListItem,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  Tooltip,
  UnorderedList,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegTrashAlt } from 'react-icons/fa';

type CalendarsListProps = {
  separatorColor: string;
};

function CalendarsSettings({ separatorColor }: CalendarsListProps) {
  const { t } = useTranslation('CalendarsSettings');
  const toast = useToast();
  const { data: cronofyAuthUrl } = useCronofyAuthUrl();
  const { data: cronofyProfiles, refetch: _refetchProfiles } = useCronofyProfiles();
  const { mutateAsync: disconnectProfile } = useCronofyDisconnectProfile();
  const { mutate: setInboundSync } = useCronofySetInboundSync();
  const { mutateAsync: setOutboundSync } = useCronofySetOutboundSync();

  // Extended Permissions hooks
  const { mutateAsync: requestExtendedPermissions } = useCronofyRequestExtendedPermissions();

  // Get all calendar IDs for permissions checking
  const allCalendarIds = useMemo(() => {
    if (!cronofyProfiles) return [];
    return cronofyProfiles.flatMap((profile) => profile.calendars.map((calendar) => calendar.id));
  }, [cronofyProfiles]);

  const { data: calendarPermissions, refetch: refetchPermissions } = useCronofyCheckCalendarPermissions(
    allCalendarIds,
    allCalendarIds.length > 0,
  );

  // Extended Permissions Manager
  const permissionsManager = useMemo(
    () => new CronofyExtendedPermissionsManager(requestExtendedPermissions),
    [requestExtendedPermissions],
  );

  const [oldCronofyProfilesLength, setOldCronofyProfilesLength] = useState<number>(cronofyProfiles?.length || 0);
  const [isFetching, setIsFetching] = useState(false);
  const [requestingPermissions, setRequestingPermissions] = useState(false);
  const [shouldCheckPermissionsAfterAdd, setShouldCheckPermissionsAfterAdd] = useState(false);

  const [reduceMotion, setReduceMotion] = useState(false);
  const refetchProfiles = useCallback(async () => {
    // Chakra UI Accordion has a bug where it doesn't start the expand animation
    // when an expanded element is dynamically added to the list. To fix this,
    // we disable animations while we are loading the new list, and re-enable
    // them after the list has been rendered.
    setReduceMotion(true);
    await _refetchProfiles();
    await refetchPermissions();
    setTimeout(() => setReduceMotion(false), 200);
  }, [_refetchProfiles, refetchPermissions]);

  const refetchProfilesAfterAdd = useCallback(async () => {
    await refetchProfiles();
  }, [refetchProfiles]);

  // Validate permissions only when component mounts
  useEffect(() => {
    if (allCalendarIds.length > 0) {
      refetchPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      oldCronofyProfilesLength === undefined ||
      cronofyProfiles?.length === oldCronofyProfilesLength ||
      !cronofyProfiles
    )
      return;

    const isProfileRemoved = cronofyProfiles.length < oldCronofyProfilesLength;
    setOldCronofyProfilesLength(cronofyProfiles.length);

    toast({
      title: isProfileRemoved ? t('toasts.calendarRemoved') : t('toasts.calendarAdded'),
      status: 'success',
      duration: 3000,
      isClosable: true,
    });

    if (isProfileRemoved) {
      setProfileIdToDelete(undefined);
      setShouldCheckPermissionsAfterAdd(false);
    }
  }, [cronofyProfiles, t, toast, oldCronofyProfilesLength]);

  const [profileIdToDelete, setProfileIdToDelete] = useState<string>();

  const handleDeleteProfile = async () => {
    if (!profileIdToDelete) return;
    try {
      setIsFetching(true);
      await disconnectProfile({ profileId: profileIdToDelete });
      await refetchProfiles();
      await handleSetOutboundSync(profileIdToDelete, null);
    } catch (error) {
      console.error(error);
    } finally {
      setIsFetching(false);
    }
  };

  const [loadingOutbountSync, setLoadingOutbountSync] = useState<string>();

  const handleSetOutboundSync = async (profileId: string, calendarId: string | null) => {
    setLoadingOutbountSync(profileId);
    // Make sure the spinner is visible for at least 1.5 seconds
    await Promise.all([
      setOutboundSync({ profileId, calendarId }),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ]);
    setLoadingOutbountSync(undefined);
  };

  // Handle Extended Permissions request
  const handleRequestExtendedPermissions = useCallback(async () => {
    if (!calendarPermissions) return;

    const calendarsNeedingPermissions = getCalendarsNeedingPermissions(calendarPermissions);

    if (!calendarsNeedingPermissions.length) {
      toast({
        title: t('toasts.allCalendarsHavePermissions'),
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // CRITICAL: Open popup IMMEDIATELY to avoid browser blocking
    // This must be the first action, before any async operations
    const popup = permissionsManager.openPermissionsPopupImmediate();

    if (!popup) {
      return;
    }

    try {
      setRequestingPermissions(true);

      const result = await permissionsManager.requestExtendedPermissionsWithPopup(
        calendarsNeedingPermissions,
        popup,
        t,
      );

      if (result.success) {
        // Refresh permissions to get updated status
        await refetchPermissions();
      } else {
        toast({
          title: t('toasts.extendedPermissionsNotGranted'),
          description: t('toasts.extendedPermissionsNotGrantedDescription'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error requesting extended permissions:', error);
      // Make sure to close popup on error
      if (!popup.closed) {
        popup.close();
      }
      toast({
        title: t('toasts.extendedPermissionsError'),
        description: t('toasts.extendedPermissionsErrorDescription'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRequestingPermissions(false);
    }
  }, [calendarPermissions, toast, t, setRequestingPermissions, permissionsManager, refetchPermissions]);

  // Check if calendars need extended permissions
  const needsExtendedPermissions = useMemo(() => {
    if (!calendarPermissions) {
      return false;
    }
    const calendarsNeedingPermissions = getCalendarsNeedingPermissions(calendarPermissions);
    return calendarsNeedingPermissions.length > 0;
  }, [calendarPermissions]);

  // Auto-request permissions after adding a calendar
  useEffect(() => {
    if (shouldCheckPermissionsAfterAdd && needsExtendedPermissions && !requestingPermissions) {
      setShouldCheckPermissionsAfterAdd(false);
      handleRequestExtendedPermissions();
    }
  }, [
    shouldCheckPermissionsAfterAdd,
    needsExtendedPermissions,
    requestingPermissions,
    handleRequestExtendedPermissions,
    calendarPermissions,
  ]);

  // Get permission status for a specific calendar
  const getCalendarPermissionStatus = (calendarId: string) => {
    if (!calendarPermissions) return null;
    return calendarPermissions.find((p) => p.calendarId === calendarId);
  };

  const openPopup = (url: string) => {
    if (!url) return;

    const width = 600;
    const height = 820;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    // Note: in order for Safari to not block the popup, the window.open() call
    // must be triggered by a user action, meaning it must be called from a
    // click event handler and it must not be async
    const newWindow = window.open(
      url,
      'Connect Calendar Account',
      `scrollbars=1,width=${width},height=${height},left=${left},top=${top}`,
    );

    if (!newWindow) return;

    // Handle popup closure detection with COOP policy support
    let isPopupClosed = false;
    let fallbackTimer: NodeJS.Timeout;

    const handlePopupClosed = () => {
      if (isPopupClosed) return;
      isPopupClosed = true;

      refetchProfilesAfterAdd();

      // In some instances, Cronofy profiles are not yet updated after the
      // popup is closed, so the refetch above doesn't pick up the updates.
      // To fix this, we refetch again after a delay. If everything goes
      // well update will be instantaneous, otherwise will come a bit later.
      setTimeout(() => refetchProfilesAfterAdd(), 2000);

      // Set this AFTER refetch completes to ensure calendarPermissions are updated
      setTimeout(() => setShouldCheckPermissionsAfterAdd(true), 500);

      if (fallbackTimer) clearTimeout(fallbackTimer);
    };

    // Primary detection method: Check window.closed with COOP error handling
    const interval = setInterval(() => {
      try {
        if (newWindow.closed) {
          handlePopupClosed();
          clearInterval(interval);
        }
      } catch (error) {
        // COOP policy blocks access to newWindow.closed
        // Fall back to focus detection method
        console.warn('COOP policy blocks window.closed access, using fallback detection');
        clearInterval(interval);
      }
    }, 500);

    // Fallback method: Detect when main window regains focus
    const handleWindowFocus = () => {
      // Small delay to ensure popup has actually closed
      setTimeout(() => {
        try {
          if (newWindow.closed) {
            handlePopupClosed();
            cleanup();
          }
        } catch (error) {
          // If we can't access newWindow.closed, assume it's closed after focus
          handlePopupClosed();
          cleanup();
        }
      }, 100);
    };

    // Safety fallback: Close detection after reasonable timeout (5 minutes)
    fallbackTimer = setTimeout(
      () => {
        console.warn('Popup detection timeout reached, assuming popup was closed');
        handlePopupClosed();
        cleanup();
      },
      5 * 60 * 1000,
    );

    const cleanup = () => {
      clearInterval(interval);
      clearTimeout(fallbackTimer);
      window.removeEventListener('focus', handleWindowFocus);
    };

    // Listen for focus events as a backup detection method
    window.addEventListener('focus', handleWindowFocus);
  };

  // Storing collapsed profiles instead of expanded ones, so if a new unknown
  // profile appears it will not be present in this list, and so will be rendered
  // expanded by default
  const [collapsedProfiles, setCollapsedProfiles] = usePersistedState(
    'rpm-settings-collapsed-profiles',
    Array<string>(),
  );
  const expandedAccordionIndexes = useMemo(
    () =>
      (cronofyProfiles ?? [])
        .map((profile, index) => (collapsedProfiles.includes(profile.id) ? false : index))
        .filter((index) => index !== false) as number[],
    [cronofyProfiles, collapsedProfiles],
  );

  const transition = '0.25s ease';

  return (
    <>
      {/* Extended Permissions Alert */}
      {needsExtendedPermissions && (
        <Box
          marginTop={rem(8)}
          borderBottom={`${rem(1)} solid ${separatorColor}`}
          borderLeft={`${rem(4)} solid`}
          borderLeftColor="amber.400"
          aria-live="polite"
          backgroundColor="amber.50"
          paddingX={rem(16)}
          paddingY={rem(16)}
          role="alert"
        >
          <HStack alignItems="start" gap={rem(8)}>
            <Icon as={IconBlock} flexShrink={0} boxSize={rem(16)} marginTop={rem(2)} color="amber.600" />
            <Box flex={1}>
              <Text marginBottom={rem(4)} color="amber.800" fontSize="sm" fontWeight={600}>
                {t('labels.extendedPermissionsRequired')}
              </Text>
              <Text
                marginBottom={rem(8)}
                color="amber.700"
                fontSize="sm"
                lineHeight="1.4"
                whiteSpace="normal"
                wordBreak="break-word"
              >
                {t('labels.extendedPermissionsDescription')}
              </Text>
              <Button
                color="amber.700"
                aria-describedby="permissions-description"
                colorScheme="yellow"
                isLoading={requestingPermissions}
                loadingText={t('buttons.grantExtendedPermissions')}
                onClick={handleRequestExtendedPermissions}
                size="sm"
                variant="outline"
              >
                {t('buttons.grantExtendedPermissions')}
              </Button>
            </Box>
          </HStack>
        </Box>
      )}

      {cronofyProfiles && cronofyProfiles.length > 0 && (
        <Accordion
          allowMultiple
          defaultIndex={expandedAccordionIndexes}
          index={expandedAccordionIndexes}
          onChange={(expandedIndexes) =>
            setCollapsedProfiles(
              cronofyProfiles
                .filter((_, index) => (expandedIndexes as number[]).includes(index) === false)
                .map((profile) => profile.id),
            )
          }
          reduceMotion={reduceMotion}
        >
          {cronofyProfiles.map((profile) => (
            <AccordionItem
              key={profile.id}
              borderBottom={`${rem(1)} solid ${separatorColor}`}
              paddingX={rem(16)}
              role="group"
            >
              {/* Calendar profile header */}
              <Box position="relative" width="full">
                <AccordionButton gap={rem(8)} display="flex" width="full" paddingX={0} paddingY={rem(16)}>
                  <Icon as={providerIcon.get(profile.provider)} width={rem(18)} />
                  <Text
                    overflow="hidden"
                    minWidth={0}
                    fontSize="lg"
                    fontWeight={500}
                    whiteSpace="nowrap"
                    textOverflow="ellipsis"
                  >
                    {profile.name}
                  </Text>
                  {profile.relinkUrl && (
                    <Icon
                      as={IconAlert}
                      // margin-right leaves room for the bin button in case the
                      // text is very long and fills the width (the bin button is
                      // positioned absolutely to avoid having a button-inside-button
                      // situation)
                      marginRight={rem(32)}
                      color="amber.400"
                    />
                  )}
                  {/* Container to add padding to leave space for bin icon in 
                      case of very long text */}
                  <Flex marginLeft="auto" paddingLeft={rem(20)}>
                    <AccordionIcon />
                  </Flex>
                </AccordionButton>
                <Tooltip label={t('labels.disconnectCalendarAccount')} placement="top">
                  <IconButton
                    position="absolute"
                    top={rem(20)}
                    right={rem(28)}
                    marginLeft="auto"
                    opacity={0}
                    _groupHover={{ opacity: 1 }}
                    transition={`opacity ${transition}`}
                    aria-label={t('ariaLabels.removeProfile')}
                    icon={<Icon as={FaRegTrashAlt} size={rem(14)} />}
                    onClick={() => setProfileIdToDelete(profile.id)}
                    size={rem(14)}
                    variant="tertiary"
                  />
                </Tooltip>
              </Box>

              {/* List of calendars */}
              <AccordionPanel padding={0}>
                {profile.relinkUrl && (
                  <HStack alignItems="start" gap={rem(8)} paddingBottom={rem(16)} color="amber.400">
                    {/* The Flex below centers the alert icon with the others  */}
                    <Flex justifyContent="center" flexShrink={0} width={rem(18)}>
                      <Icon as={IconAlert} boxSize={rem(14)} />
                    </Flex>
                    <Text width="full" fontSize="sm">
                      {t('labels.reconnectAccount')}
                      <br />
                      <Link textDecoration="underline" onClick={() => openPopup(profile.relinkUrl)}>
                        {t('labels.clickToRelink')}
                      </Link>
                      .
                    </Text>
                  </HStack>
                )}
                {profile.initializing ? (
                  <Text
                    width="full"
                    paddingTop={rem(8)}
                    paddingBottom={rem(24)}
                    color="gray.400"
                    fontSize="md"
                    textAlign="center"
                  >
                    {t('labels.loadingCalendars')}
                    <Spinner marginLeft={rem(10)} size="xs" />
                  </Text>
                ) : (
                  <>
                    <UnorderedList
                      flexDirection="column"
                      gap={rem(8)}
                      display="flex"
                      margin={0}
                      paddingTop={rem(4)}
                      aria-label={t('ariaLabels.readEventsFrom')}
                    >
                      {profile.calendars.map((calendar) => {
                        const permissionStatus = getCalendarPermissionStatus(calendar.id);
                        const hasUnrestricted = permissionStatus?.hasUnrestrictedAccess || false;

                        return (
                          <ListItem key={calendar.id} alignItems="center" gap={rem(8)} display="flex">
                            <Checkbox
                              sx={{ span: { overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 'md' } }}
                              flex={1}
                              minWidth={0}
                              isChecked={calendar.inboundSync}
                              onChange={() =>
                                setInboundSync({
                                  profileId: profile.id,
                                  calendarId: calendar.id,
                                  enabled: !calendar.inboundSync,
                                })
                              }
                              size="sm"
                            >
                              {calendar.name}
                            </Checkbox>
                            {/* Permission status indicator */}
                            {!hasUnrestricted && (
                              <Tooltip hasArrow label={t('labels.extendedPermissionsTooltip')} placement="top">
                                <Badge
                                  alignItems="center"
                                  gap={rem(4)}
                                  display="flex"
                                  fontSize="xs"
                                  cursor="help"
                                  colorScheme="yellow"
                                  variant="subtle"
                                >
                                  <Icon as={IconBlock} boxSize={rem(10)} />
                                  {t('labels.limitedPermissions')}
                                </Badge>
                              </Tooltip>
                            )}
                          </ListItem>
                        );
                      })}
                    </UnorderedList>

                    {/* Dropdown for outbound sync */}
                    <Text
                      as="span"
                      display="flex"
                      height={rem(15)} // Keeps height stable when spinner appears
                      marginTop={rem(20)}
                      marginBottom={rem(8)}
                      color="gray.400"
                      fontSize="xs"
                      fontWeight="900"
                      textTransform="uppercase"
                    >
                      {t('labels.writeRPMEventsTo')}
                      {loadingOutbountSync === profile.id && <Spinner marginLeft="auto" size="xs" />}
                    </Text>
                    <Menu matchWidth>
                      <MenuButton
                        as={Button}
                        sx={{
                          _disabled: { opacity: 0.6 },
                          'span:first-of-type': { overflow: 'hidden', textOverflow: 'ellipsis' },
                        }}
                        justifyContent="space-between"
                        display="flex"
                        width="full"
                        marginBottom={rem(24)}
                        fontSize="md"
                        textAlign="left"
                        transition={`opacity ${transition}`}
                        isDisabled={loadingOutbountSync === profile.id}
                        rightIcon={<Icon as={IconArrowDownFull} boxSize={rem(7)} />}
                        variant="secondary"
                      >
                        {profile.outboundSyncCalendarId
                          ? profile.calendars.find((calendar) => calendar.id === profile.outboundSyncCalendarId!)?.name
                          : t('labels.none')}
                      </MenuButton>
                      <MenuList
                        sx={{
                          // Style of the menu items
                          button: {
                            justifyContent: 'space-between',
                            background: 'none',
                            display: 'flex',
                            _hover: { backgroundColor: 'background-tertiary' },
                            'span:first-of-type': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              minWidth: 0,
                            },
                            'span:last-of-type': {
                              flexShrink: 0,
                              width: rem(20),
                              textAlign: 'right',
                            },
                          },
                        }}
                        padding={rem(8)}
                        fontSize="md"
                        borderWidth={rem(1)}
                        borderStyle="solid"
                        borderColor="gray.700"
                        borderRadius="4"
                        backgroundColor="background-primary"
                      >
                        <MenuItem onClick={() => handleSetOutboundSync(profile.id, null)}>
                          <Box as="span">{t('labels.none')}</Box>
                          <Box as="span">{profile.outboundSyncCalendarId === null && '✓'}</Box>
                        </MenuItem>
                        {profile.calendars
                          ?.filter((calendar) => !calendar.readonly) //Don't show readonly calendars
                          .map((calendar) => (
                            <MenuItem
                              key={calendar.id}
                              onClick={() =>
                                profile.outboundSyncCalendarId !== calendar.id &&
                                handleSetOutboundSync(profile.id, calendar.id)
                              }
                            >
                              <Box as="span">{calendar.name}</Box>
                              <Box as="span">{profile.outboundSyncCalendarId === calendar.id && '✓'}</Box>
                            </MenuItem>
                          ))}
                      </MenuList>
                    </Menu>
                  </>
                )}
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      <Box borderBottom={`${rem(1)} solid ${separatorColor}`} paddingX={rem(16)} paddingY={rem(24)}>
        <Button
          width="full"
          height="unset"
          fontSize="inherit"
          fontWeight="inherit"
          leftIcon={<IconPlus width={rem(24)} height={rem(24)} />}
          onClick={() => cronofyAuthUrl && openPopup(cronofyAuthUrl)}
          paddingY={rem(16)}
          variant="secondary"
        >
          {t('buttons.addCalendarAccount')}
        </Button>
      </Box>

      <DialogModal
        isOpen={profileIdToDelete !== undefined}
        onCancel={() => setProfileIdToDelete(undefined)}
        title={t('confirmation.title')}
        message={t('confirmation.disconnectMessage')}
        confirmVariant="danger"
        confirmText={t('confirmation.disconnectButton')}
        onConfirm={handleDeleteProfile}
        isButtonConfirmLoading={isFetching}
      />
    </>
  );
}

export default CalendarsSettings;
